import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { SKIP_AUTH } from '../../interceptors/auth.interceptor';

type MarkdownState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; html: SafeHtml; empty: boolean }
  | { kind: 'error'; title: string; detail: string };

/**
 * Turns one error into copy that names both the cause and the way out, rather
 * than a bare status code the reader has to interpret.
 */
function describe(error: HttpErrorResponse): { title: string; detail: string } {
  switch (error.status) {
    case 403:
      return {
        title: 'This preview link has expired',
        detail: 'Signed links are short-lived. Reload the page to get a fresh one.'
      };
    case 404:
      return {
        title: 'The markdown file is no longer stored',
        detail: 'It may have been removed since this page loaded. Reprocess the document to regenerate it.'
      };
    case 0:
      return {
        title: 'Could not reach storage',
        detail: 'The request never completed — check your connection, then retry.'
      };
    default:
      return {
        title: `Could not load the markdown (${error.status})`,
        detail: error.statusText || 'An unexpected error occurred while fetching the file.'
      };
  }
}

/**
 * Renders the markdown serialization the OCR pipeline actually reads.
 *
 * Mistral is configured with `tableFormat: 'html'`, so the payload is markdown
 * with raw HTML tables embedded in it — escaping that HTML would defeat the
 * purpose of the view, which is to show engineers exactly what the extraction
 * agents saw.
 */
@Component({
  selector: 'app-markdown-view',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './markdown-view.component.html',
  styleUrl: './markdown-view.component.scss'
})
export class MarkdownViewComponent {
  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);

  /** Signed S3 URL of the markdown file; `null` when none was ever produced. */
  readonly url = input<string | null>(null);

  /** Bumped by Retry so the same URL re-fetches. */
  private readonly attempt = signal(0);

  private readonly request = computed(() => ({ url: this.url(), attempt: this.attempt() }));

  readonly state = toSignal(
    toObservable(this.request).pipe(
      switchMap(({ url }) => {
        if (!url) {
          return of<MarkdownState>({ kind: 'idle' });
        }
        return this.http
          .get(url, {
            responseType: 'text',
            // A presigned URL carries its own credentials; S3 rejects a request
            // that also sends our bearer token, and the extra header would
            // force a CORS preflight on top.
            context: new HttpContext().set(SKIP_AUTH, true)
          })
          .pipe(
            map(markdown => this.render(markdown)),
            catchError((error: HttpErrorResponse) => of<MarkdownState>({ kind: 'error', ...describe(error) })),
            startWith<MarkdownState>({ kind: 'loading' })
          );
      })
    ),
    { initialValue: { kind: 'idle' } as MarkdownState }
  );

  /* Narrowed views of `state`, so the template can branch without casts. */
  readonly loading = computed(() => this.state().kind === 'loading');
  readonly failure = computed(() => {
    const state = this.state();
    return state.kind === 'error' ? state : null;
  });
  readonly content = computed(() => {
    const state = this.state();
    return state.kind === 'loaded' && !state.empty ? state.html : null;
  });
  readonly isEmpty = computed(() => {
    const state = this.state();
    return state.kind === 'loaded' && state.empty;
  });

  retry(): void {
    this.attempt.update(n => n + 1);
  }

  private render(markdown: string): MarkdownState {
    const html = marked.parse(markdown, { async: false, gfm: true, breaks: false });
    const clean = DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      // Mistral's HTML tables lean on merged cells; Angular's own sanitizer
      // drops these attributes, which is why DOMPurify — not Angular — is the
      // trust boundary here and the result is passed through `bypassSecurity…`.
      ADD_ATTR: ['colspan', 'rowspan']
    });

    return {
      kind: 'loaded',
      html: this.sanitizer.bypassSecurityTrustHtml(clean),
      empty: clean.trim().length === 0
    };
  }
}
